import React, { useState } from 'react';
import { Search, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

interface Tool {
  name: string;
  description: string;
  category: string;
  image: string;
  url: string;
  price: string;
  discountCode?: string;
  affiliateLink?: string;
  recommended: boolean;
  tags: string[];
}

const tools: Tool[] = [
  {
    name: 'Hostinger',
    description: 'Reliable and affordable web hosting with excellent performance and customer support.',
    category: 'Hosting & Deployment',
    image: 'https://upload.wikimedia.org/wikipedia/commons/e/e5/Hostinger_Logotype.png',
    url: 'https://hostinger.com',
    price: 'From $2.99/month',
    affiliateLink: 'https://go.ates.me/hostinger',
    recommended: true,
    tags: ['Hosting', 'Domains', 'WordPress']
  },
  {
    name: 'DigitalOcean',
    description: 'Cloud platform for developers. Simple yet powerful infrastructure with predictable pricing.',
    category: 'Cloud Services',
    image: 'https://www.digitalocean.com/_next/static/media/do-logo.d3eb8550.svg',
    url: 'https://digitalocean.com',
    price: 'From $4/month',
    affiliateLink: 'https://go.ates.me/digitalocean',
    recommended: true,
    tags: ['Cloud', 'VPS', 'Kubernetes']
  },
  {
    name: 'Wise',
    description: 'International money transfers made easy. Save on fees with real exchange rates.',
    category: 'Business Tools',
    image: 'https://wise.com/public-resources/assets/logos/wise/brand_logo.svg',
    url: 'https://wise.com',
    price: 'Variable fees based on transfer',
    affiliateLink: 'https://go.ates.me/transferwise',
    recommended: true,
    tags: ['Finance', 'Payments', 'International']
  },
  {
    name: 'Revolut',
    description: 'Digital banking solution with multi-currency accounts, instant transfers, and powerful financial tools.',
    category: 'Business Tools',
    image: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d6/Revolut.svg/150px-Revolut.svg.png',
    url: 'https://revolut.com',
    price: 'Free plan available, Premium from $9.99/month',
    affiliateLink: 'https://revolut.com/referral/?referral-code=huseyi4v',
    recommended: true,
    tags: ['Finance', 'Banking', 'International']
  },
  {
    name: 'Shopify',
    description: 'Complete e-commerce platform to start, grow, and manage your business.',
    category: 'E-commerce',
    image: 'https://cdn.shopify.com/assets/images/logos/shopify-bag.png',
    url: 'https://shopify.com',
    price: 'From $29/month',
    affiliateLink: 'https://shopify.pxf.io/c/2082513/1061744/13624',
    recommended: true,
    tags: ['E-commerce', 'Business', 'Online Store']
  },
  {
    name: 'Jungle Scout',
    description: 'All-in-one platform for selling on Amazon. Market research, product finder, and analytics.',
    category: 'E-commerce Tools',
    image: 'https://www.junglescout.com/wp-content/themes/wolfpack-v2/dist/images/js-menu-logo_063e86b5659db11ede2f.png',
    url: 'https://junglescout.com',
    price: 'From $29/month',
    affiliateLink: 'https://go.ates.me/junglescout',
    recommended: true,
    tags: ['Amazon', 'FBA', 'Market Research']
  },
  {
    name: 'Windscribe VPN',
    description: 'Secure and private internet access. Browse safely with servers worldwide.',
    category: 'Security',
    image: 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c1/Windscribe_client.png/250px-Windscribe_client.png',
    url: 'https://windscribe.com',
    price: 'Free tier available, from $4.08/month',
    affiliateLink: 'https://go.ates.me/windscribeVPN',
    recommended: true,
    tags: ['VPN', 'Security', 'Privacy']
  },
  {
    name: 'VS Code',
    description: 'The best code editor with powerful extensions, great Git integration, and excellent TypeScript support.',
    category: 'Development',
    image: 'https://code.visualstudio.com/assets/images/code-stable.png',
    url: 'https://code.visualstudio.com',
    price: 'Free',
    recommended: true,
    tags: ['Editor', 'Development', 'IDE']
  },
  {
    name: 'Supabase',
    description: 'Open source Firebase alternative. Great for building scalable applications with real-time features.',
    category: 'Database & Backend',
    image: 'https://frontend-assets.supabase.com/www/df9c1bc0174a/_next/static/media/supabase-logo-wordmark--dark.b36ebb5f.png',
    url: 'https://supabase.com',
    price: 'Free tier available, from $25/month',
    affiliateLink: 'https://supabase.com/?ref=cemates',
    recommended: true,
    tags: ['Database', 'Backend', 'Real-time']
  }
];

export const Tools: React.FC = () => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = ['all', ...Array.from(new Set(tools.map(tool => tool.category)))];

  const filteredTools = tools.filter(tool => {
    const matchesSearch = tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tool.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || tool.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="pt-24 pb-20">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-semibold mb-4">
            {t('tools', 'title')}
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t('tools', 'subtitle')}
          </p>
        </div>

        {/* Support Notice */}
        <div className="mb-12 bg-muted/50 rounded-lg p-6 text-center">
          <p className="text-sm text-muted-foreground">
            {t('tools', 'affiliateNotice')}
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-12 space-y-6">
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <input
              type="text"
              placeholder={t('tools', 'searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-foreground text-sm"
            />
          </div>

          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                  selectedCategory === category
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {category === 'all' ? t('tools', 'all') : category}
              </button>
            ))}
          </div>
        </div>

        {/* Tools Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTools.map(tool => (
            <div
              key={tool.name}
              className="bg-card rounded-lg border border-border overflow-hidden hover:border-foreground/20 transition-colors"
            >
              <div className="aspect-video w-full overflow-hidden bg-muted/30 flex items-center justify-center p-6">
                <img
                  src={tool.image}
                  alt={tool.name}
                  className="max-h-12 w-auto object-contain"
                />
              </div>
              <div className="p-5">
                <h3 className="text-base font-medium mb-2">{tool.name}</h3>
                <p className="text-muted-foreground text-sm mb-3 line-clamp-2 leading-relaxed">{tool.description}</p>
                <p className="text-xs text-muted-foreground mb-4">{tool.price}</p>
                <a
                  href={tool.affiliateLink || tool.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm font-medium hover:underline underline-offset-4"
                >
                  {t('tools', 'learnMore')} <ArrowRight className="ml-1 w-3 h-3" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
